import React, { useMemo } from 'react';
import { Header } from '../../common/header.component';
import { AlertService } from '../../../services/alert.service';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Control,
  Controller,
  FieldPath,
  FieldPathValue,
  useForm,
  UseFormRegister,
  useFormState,
} from 'react-hook-form';
import { required } from '../../../utils/validations';
import { InputWrapper } from '../../common/input-wrapper';
import { api, fetcher } from '../../../services/api';
import { uploadMediaInputFiles } from '../../../services/file-service';
import { MediaInput, MediaInputValue } from '../../common/media-input';
import { FieldValues } from 'react-hook-form';
import get from 'lodash/get';
import { getCurrencySymbol } from '../../../utils/common.utils';
import { CleanPurchaseOrder } from '../../../export-types/cleaned-types';
import useSWR from 'swr';
import ReactSelect from 'react-select';
import { PurchaseOrderItem } from '../delivery/delivery-order-create-screen.component';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePurchaseOrdersByInternalId } from '../../../api/use-purchase-orders-by-internal-id';

type ParcelFormData = {
  orderId?: string;
  parcel: {
    name: string;
    trackNumber: string;
    requestPlaces: string;
    requestCount: string;
    productPrice: string;
    comment: string;
    requestFiles: MediaInputValue[];
  };
};

export const AdminRelatedParcelCreateScreen: React.FC = () => {
  const { id } = useParams();
  const { data: order } = useSWR<CleanPurchaseOrder>(
    `/purchase-order/${id}`,
    fetcher,
  );
  const navigate = useNavigate();

  if (!order) {
    return null;
  }

  const onSubmit = async (form: ParcelFormData) => {
    const requestFilesResponse = await uploadMediaInputFiles(
      form.parcel.requestFiles,
    );

    const {
      data: { parcel },
    } = await api.post(`/parcels/admin/related/${id}`, {
      name: form.parcel.name.trim(),
      trackNumber: form.parcel.trackNumber.trim(),
      requestPlaces: Number(form.parcel.requestPlaces),
      requestCount: Number(form.parcel.requestCount),
      productPrice: form.parcel.productPrice,
      comment: form.parcel.comment.trim(),
      requestFiles: requestFilesResponse.map((response) => ({
        id: response.file.id,
      })),
    });

    AlertService.success(
      <div>
        <div className="fw-medium">Заказ {parcel.internalId} создан</div>
        <div className="fs-14">
          За всеми изменениями по заказу вы можете наблюдать в разделе “Выкупы”.
        </div>
      </div>,
    );

    navigate(-1);
  };

  return (
    <>
      <Header />
      <div className="container" style={{ marginTop: -10 }}>
        <h1 className="mb-4">
          <FormattedMessage
            defaultMessage="Создать связанную с V{value} посылку"
            id="adminRelatedParcelCreate.title"
            description="Title for creating a related parcel"
            values={{ value: order.internalId }}
          />
        </h1>
        <ParcelForm order={order} onSubmit={onSubmit} />
      </div>
    </>
  );
};

export const AdminRelatedParcelAddExitedScreen: React.FC = () => {
  const { id } = useParams();
  const { data: order } = useSWR<CleanPurchaseOrder>(
    `/purchase-order/${id}`,
    fetcher,
  );
  const navigate = useNavigate();

  if (!order) {
    return null;
  }

  const onSubmit = async (form: ParcelFormData) => {
    const requestFilesResponse = await uploadMediaInputFiles(
      form.parcel.requestFiles,
    );

    const {
      data: { parcel },
    } = await api.post(`/parcels/admin/related/${id}/add-existed`, {
      orderId: form.orderId,
      name: form.parcel.name.trim(),
      trackNumber: form.parcel.trackNumber.trim(),
      requestPlaces: Number(form.parcel.requestPlaces),
      requestCount: Number(form.parcel.requestCount),
      productPrice: form.parcel.productPrice,
      comment: form.parcel.comment.trim(),
      requestFiles: requestFilesResponse.map((response) => ({
        id: response.file.id,
      })),
    });

    AlertService.success(
      <div>
        <div className="fw-medium">Заказ {parcel.internalId} создан</div>
        <div className="fs-14">
          За всеми изменениями по заказу вы можете наблюдать в разделе “Выкупы”.
        </div>
      </div>,
    );

    navigate(-1);
  };
  return (
    <>
      <Header />
      <div className="container" style={{ marginTop: -10 }}>
        <h1 className="mb-4">
          <FormattedMessage
            defaultMessage="Добавить связанную с V{value} посылку"
            id="adminRelatedParcelAddExisted.title"
            description="Title for adding an existing related parcel"
            values={{ value: order.internalId }}
          />
        </h1>
        <ParcelForm order={order} onSubmit={onSubmit} isAddExisted={true} />
      </div>
    </>
  );
};

type ParcelFormProps = {
  order: CleanPurchaseOrder;
  defaultValues?: Partial<ParcelFormData>;
  isAddExisted?: boolean;
  onSubmit: (form: ParcelFormData) => Promise<void>;
};

const ParcelForm = (props: ParcelFormProps) => {
  const {
    reset,
    register,
    control,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm<ParcelFormData>({
    defaultValues: props.defaultValues,
  });
  const intl = useIntl();
  const [selectedOrder, setSelectedOrder] =
    React.useState<CleanPurchaseOrder>();

  const [readOnly, setReadOnly] = React.useState(false);

  const { data: orders } = usePurchaseOrdersByInternalId({
    take: 100,
    all: true,
    internalId: props.order.user?.internalId?.toString(),
    parentOrderId: props.order.id,
  });

  async function onSubmit(form: ParcelFormData) {
    await props.onSubmit(form);
  }

  const address = useMemo(() => {
    const direction = props.order.supplier;
    const internalId = props.order.user?.internalId || '';

    return direction?.addressForParcels.replaceAll(
      '{user.internalId}',
      String(internalId),
    );
  }, [props.order]);

  const currencySupplier = props.order.supplier?.purchaseCurrency || '';

  return (
    <form
      className="row justify-content-center pt-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="col-7">
        {props.isAddExisted ? (
          <InputWrapper
            title={intl.formatMessage({
              defaultMessage: 'Выберите заказ для связывания',
              id: 'adminRelatedParcelForm.chooseOrder',
              description: 'Заголовок поля выбора заказа для связывания',
            })}
            className="fw-medium mb-4"
          >
            <ReactSelect
              className="fw-400"
              classNamePrefix="custom-select"
              placeholder={intl.formatMessage({
                defaultMessage: 'Добавить заказ',
                id: 'btn.label.select',
                description: 'Плейсхолдер для кнопки выбора заказа',
              })}
              options={
                orders?.items.map((order) => ({
                  ...order,
                  label: order.internalId,
                })) ?? []
              }
              formatOptionLabel={(order) => {
                if (!order) return null;
                return <PurchaseOrderItem order={order} />;
              }}
              value={selectedOrder}
              isSearchable={true}
              onChange={(value) => {
                if (value) {
                  setSelectedOrder(value);
                  setReadOnly(value.status !== 'onTheWayToStock');

                  reset({
                    orderId: value.id,
                    parcel: {
                      name: value.name || '',
                      trackNumber: value.trackNumber || '',
                      requestPlaces: value.requestPlaces?.toString() || '',
                      requestCount: value.requestCount?.toString() || '',
                      productPrice: value.productPrice || '',
                      comment: value.comment || '',
                      requestFiles: value.requestFilesV2.map((file) => ({
                        type: 'exist',
                        file,
                      })),
                    },
                  });
                }
              }}
              noOptionsMessage={() => (
                <span>
                  {intl.formatMessage({
                    defaultMessage: 'Нет заказов',
                    id: 'DeliveryOrderCreate.input-label.noOrders',
                    description: 'Пояснение к полю ввода',
                  })}
                </span>
              )}
            />
          </InputWrapper>
        ) : null}

        {readOnly ? (
          <div className={'mb-3 fw-600'}>
            Эта посылка находится в архиве, поэтому вы не можете ее изменить
          </div>
        ) : null}

        {props.isAddExisted && !selectedOrder ? null : (
          <div className="mb-4">
            <ParcelItemForm
              readOnly={readOnly}
              register={register}
              control={control}
              path="parcel"
              currencySupplier={currencySupplier}
            />
          </div>
        )}
      </div>
      <div className="col-5">
        <div className="sticky-top ">
          <div className="bg-gray-50 p-4">
            <h3 className="fs-21 fw-500">
              <FormattedMessage
                defaultMessage="Ваш адрес для посылок"
                id="adminRelatedParcelForm.addressTitle"
                description="Title for parcel address section"
              />
            </h3>
            <div className="color-gray-400 fs-14 lh-sm">
              <FormattedMessage
                defaultMessage="Укажите этот адрес в форме заказа товара на сайте или отправьте поставщику"
                id="adminRelatedParcelForm.addressDescription"
                description="Description for how to use the parcel address"
              />
            </div>
            <div className="mb-4 whitespace-pre-line leading-loose">
              {address}
            </div>
            <button
              type="submit"
              className="btn btn-success w-100"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Сохранить"
                  id="adminRelatedParcelForm.save"
                  description="Save button text"
                />
              )}
            </button>
            <div className="fs-14 color-gray-400 lh-sm mt-3">
              <FormattedMessage
                defaultMessage="Размещая заказ, вы соглашаетесь с правилами пользования сервисом"
                id="adminRelatedParcelForm.addressInfo"
                description="Terms of service agreement text"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

type ParcelItemValue = {
  name: string;
  trackNumber: string;
  requestPlaces: string;
  requestCount: string;
  productPrice: string;
  comment: string;
  requestFiles: MediaInputValue[];
};

type ParcelItemFormProps<TFieldValues extends FieldValues, TContext = any> = {
  readOnly?: boolean;
  register: UseFormRegister<TFieldValues>;
  control: Control<TFieldValues, TContext>;
  currencySupplier: string;
  path: keyof {
    [P in FieldPath<TFieldValues> as FieldPathValue<
      TFieldValues,
      P
    > extends ParcelItemValue
      ? P
      : never]: P;
  };
};

const ParcelItemForm = <TFieldValues extends FieldValues, TContext = any>(
  props: ParcelItemFormProps<TFieldValues, TContext>,
) => {
  const { control, register, currencySupplier } = props;
  const { errors } = useFormState({ control });
  const intl = useIntl();

  return (
    <div className="card border-0 rounded">
      <div className="card-header bg-gray-50 border-0 p-3">
        <InputWrapper
          error={
            // @ts-ignore todo
            get(errors, props.path)?.name?.message
          }
        >
          <div className="row g-1">
            <div className="col">
              <input
                type="text"
                className="form-control"
                {...register(
                  `${String(props.path)}.name` as FieldPath<TFieldValues>,
                )}
                disabled={props.readOnly}
              />
            </div>
          </div>
        </InputWrapper>
      </div>

      <div className="card-body border border-gray-200 border-top-0 rounded-bottom">
        <InputWrapper
          title={intl.formatMessage({
            defaultMessage: 'Трек номер',
            id: 'adminRelatedParcelForm.trackNumber',
            description: 'Label for track number input',
          })}
          className="mb-4"
          error={
            // @ts-ignore todo
            get(errors, props.path)?.trackNumber?.message
          }
          required
        >
          <input
            className="form-control"
            type="text"
            disabled={props.readOnly}
            {...register(
              `${String(props.path)}.trackNumber` as FieldPath<TFieldValues>,
              {
                ...required,
              },
            )}
          />
        </InputWrapper>
        <InputWrapper
          title={intl.formatMessage({
            defaultMessage: 'Количество мест',
            id: 'adminRelatedParcelForm.quantityUnits',
            description: 'Label for quantity of units input',
          })}
          className="mb-4"
          error={
            // @ts-ignore todo
            get(errors, props.path)?.requestPlaces?.message
          }
          required
        >
          <div className="input-group">
            <input
              className="form-control"
              type="number"
              disabled={props.readOnly}
              min={1}
              step={1}
              {...register(
                `${String(props.path)}.requestPlaces` as FieldPath<TFieldValues>,
                {
                  ...required,
                },
              )}
            />
            <span className="input-group-text">шт</span>
          </div>
        </InputWrapper>

        <InputWrapper
          title={intl.formatMessage({
            defaultMessage: 'Количество товаров (необязательно)',
            id: 'adminRelatedParcelForm.numberProducts',
            description: 'Label for optional number of products input',
          })}
          className="mb-4"
          error={
            // @ts-ignore todo
            get(errors, props.path)?.requestCount?.message
          }
        >
          <div className="input-group">
            <input
              className="form-control"
              type="number"
              min={1}
              step={1}
              disabled={props.readOnly}
              {...register(
                `${String(props.path)}.requestCount` as FieldPath<TFieldValues>,
              )}
            />
            <span className="input-group-text">шт</span>
          </div>
        </InputWrapper>
        <InputWrapper
          title={intl.formatMessage({
            defaultMessage: 'Стоимость товаров',
            id: 'adminRelatedParcelForm.priceGoods',
            description: 'Label for goods price input',
          })}
          className="mb-4"
          error={
            // @ts-ignore todo
            get(errors, props.path)?.productPrice?.message
          }
          required
        >
          <div className="input-group">
            <input
              className="form-control"
              type="number"
              min={1}
              step={0.01}
              disabled={props.readOnly}
              {...register(
                `${String(props.path)}.productPrice` as FieldPath<TFieldValues>,
                {
                  ...required,
                },
              )}
            />
            <span className="input-group-text">
              {getCurrencySymbol(currencySupplier)}
            </span>
          </div>
          <div className="fs-14 color-gray-400 mt-1">
            {intl.formatMessage({
              defaultMessage:
                'Необходима для расчёта страховки при оформлении доставки.',
              id: 'adminRelatedParcelForm.priceGoodsInfo',
              description:
                'Information about price goods requirement for insurance',
            })}
          </div>
        </InputWrapper>
        {!props.readOnly ? (
          <Controller
            control={control}
            rules={{ ...required }}
            name={
              `${String(props.path)}.requestFiles` as FieldPath<TFieldValues>
            }
            render={({ field, fieldState }) => (
              <InputWrapper
                title={intl.formatMessage({
                  defaultMessage: 'Фотографии товаров',
                  id: 'adminRelatedParcelForm.images',
                  description: 'Label for product images input',
                })}
                className="mb-4"
                required
                error={fieldState.error?.message}
              >
                <MediaInput {...field} multiple />
              </InputWrapper>
            )}
          />
        ) : null}

        <InputWrapper
          title={intl.formatMessage({
            defaultMessage: 'Комментарий',
            id: 'adminRelatedParcelForm.comment',
            description: 'Label for comment input',
          })}
          className="mb-3"
        >
          <textarea
            className="form-control fs-16 lh-sm"
            disabled={props.readOnly}
            {...register(
              `${String(props.path)}.comment` as FieldPath<TFieldValues>,
            )}
          />
        </InputWrapper>
      </div>
    </div>
  );
};
